import {
  type ComponentPropsWithoutRef,
  createElement,
  type ElementType,
  type MouseEvent,
  type ReactNode,
} from "react";

import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { Spinner } from "../Loaders/LoadingSpinner/Spinner";

// Helper to extract props from the component passed to 'as', excluding ThemedIconButton's own props
type PropsOf<C extends ElementType> = ComponentPropsWithoutRef<C>;

export type ThemedIconButtonProps<C extends ElementType = "button"> = Omit<
  // Omit props handled by ThemedIconButton itself to avoid conflicts please extend this type when adding new props
  PropsOf<C>,
  "as" | "children" | "className" | "disabled" | "onClick" | "role"
> & {
  // Default to 'button'
  ariaLabel?: string;
  as?: C;
  className?: string;
  disabled?: boolean;
  icon: ReactNode;
  isLoading?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  variant?: "background" | "noBackground" | "primary";
  role?: "button" | "reset" | "submit";
  size?: "extraSmall" | "large" | "medium" | "small";
  dataTestId: string;
};

export const ThemedIconButton = <C extends ElementType = "button">({
  ariaLabel,
  as,
  className,
  disabled,
  icon,
  isLoading,
  role,
  onClick,
  variant = "background",
  size = "small",
  dataTestId,
  ...props
}: ThemedIconButtonProps<C>) => {
  const Component = as || "button";

  const baseStyles =
    "inline-flex items-center justify-center rounded-md defaultTransition outline-none";

  const sizeStyles = {
    large: "h-11 w-11",
    medium: "h-10 w-10",
    small: "h-8 w-8",
    extraSmall: "h-5 w-5",
  };

  const variantStyles = {
    primary: `
      shadow-card
      enabled:hover:shadow-tooltip
      active:shadow-none
      disabled:shadow-none

      bg-button/primary/background/default
      text-button/primary/label/default
      enabled:hover:bg-button/primary/background/hover
      active:bg-button/primary/background/pressed
      disabled:bg-button/primary/background/default/50
      disabled:text-text/disabled
      focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-active disabled:focus-visible:ring-0
    `,
    background: `
      shadow-card
      bg-icon-button/background/default
      enabled:hover:bg-icon-button/background/hover
      active:bg-icon-button/background/pressed
      border border-icon-button/background/border
      disabled:bg-background/brand/opacity
      focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-default disabled:focus-visible:ring-0
    `,
    noBackground: `
      enabled:hover:bg-icon-button/no-background/hover
      active:bg-icon-button/no-background/pressed
      disabled:text-text/disabled
      focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-default disabled:focus-visible:ring-0
    `,
  };

  const iconVariantStyles = {
    primary: "text-button/primary/foreground/default",
    background:
      "text-icon-button/background/foreground fill-icon-button/background/foreground",
    noBackground:
      "text-icon-button/no-background/foreground fill-icon-button/no-background/foreground",
  };
  const iconColorClass = iconVariantStyles[variant];

  const getEffectiveRole = (): string | undefined => {
    if (Component === "button" && !role) {
      return "button";
    }

    return role;
  };

  const effectiveRole = getEffectiveRole();
  const effectiveType = "type" in props ? props.type : "button";

  return createElement(
    Component,
    {
      "aria-label": ariaLabel,
      ["data-testid"]: dataTestId,
      className: cnTwMerge(
        { "cursor-not-allowed opacity-50": disabled },
        baseStyles,
        sizeStyles[size],
        variantStyles[variant],
        className,
      ),
      disabled: disabled ?? isLoading,
      onClick: isTruthy(disabled) || isTruthy(isLoading) ? undefined : onClick,
      role: effectiveRole,
      ...(effectiveType && { type: effectiveType }),
      ...props,
    },

    <span
      className={cnTwMerge(iconColorClass, {
        "w-11 h-11 p-[10px] flex items-center justify-center":
          size !== "extraSmall",
      })}
    >
      {isTruthy(isLoading) ? <Spinner asIcon /> : icon}
    </span>,
  );
};
